import * as React from 'react'
import * as SelectPrimitive from '@radix-ui/react-select'
import {
  Check,
  ChevronDown,
  ChevronUp,
  Info,
  Loader2,
  TriangleAlert,
  X,
} from 'lucide-react'

import { cn } from '@ui/lib/utils'
import { Checkbox } from './checkbox'
import { ChipsWrapper, ChipsItem } from './chips'

const SelectRoot = SelectPrimitive.Root as React.FC<
  SelectPrimitive.SelectProps & { ref?: React.ElementRef<never> }
>
const Select = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>
>(({ ...props }, ref) => {
  return <SelectRoot ref={ref} {...props} />
})
Select.displayName = SelectPrimitive.Root.displayName

const SelectGroup = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Group>
>((props, ref) => <SelectPrimitive.Group ref={ref} {...props} />)
SelectGroup.displayName = SelectPrimitive.Group.displayName

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>((props, ref) => <SelectPrimitive.Value ref={ref} {...props} />)
SelectValue.displayName = SelectPrimitive.Value.displayName

const SelectTriggerWrapper = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    caption?: React.ReactNode
    error?: React.ReactNode
    icon?: React.ReactNode
    children: React.ReactNode
    renderCaret?: () => React.ReactNode
  }
>(
  (
    { className, children, caption, error, icon, renderCaret, ...props },
    ref,
  ) => {
    const renderCaption = () => {
      if (!caption && !error) return null

      if (error) {
        return (
          <div className="mt-xxs flex items-center gap-xxxs text-sm text-red-600">
            <TriangleAlert size={16} />
            {error}
          </div>
        )
      }

      return (
        <div className="mt-xxs flex items-center gap-xxxs text-sm text-gray-500">
          <Info size={16} />
          {caption}
        </div>
      )
    }

    const caret = renderCaret ? (
      renderCaret()
    ) : (
      <ChevronDown
        size={20}
        className="transition-transform duration-200 group-data-[state=open]/select:rotate-180"
      />
    )
    return (
      <div className="w-full">
        <SelectPrimitive.Trigger
          ref={ref}
          className={cn(
            'group/select flex h-l w-full items-center justify-between rounded-m border bg-white p-xxs text-sm',
            'ring-offset-white placeholder:text-gray-500 focus:outline-none focus-visible:ring-gray-700',
            'focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50',
            error ? 'border-red-500' : 'border-gray-200',
            className,
          )}
          {...props}
        >
          <div
            className={cn('flex w-full flex-1 items-center gap-xxs', {
              'max-w-[calc(100%-20px)]': caret,
            })}
          >
            {icon && (
              <span
                className={cn('text-gray-500', {
                  'text-red-500': error,
                })}
              >
                {icon}
              </span>
            )}
            {children}
          </div>
          <SelectPrimitive.Icon asChild>{caret}</SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        {renderCaption()}
      </div>
    )
  },
)
SelectTriggerWrapper.displayName = 'SelectTriggerWrapper'

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    caption?: React.ReactNode
    error?: React.ReactNode
    icon?: React.ReactNode
    renderCaret?: () => React.ReactNode
  }
>(({ className, children, caption, error, icon, ...props }, ref) => {
  return (
    <SelectTriggerWrapper
      ref={ref}
      className={cn('[&>span]:line-clamp-1 [&>span]:text-left', className)}
      caption={caption}
      error={error}
      icon={icon}
      {...props}
    >
      {children}
    </SelectTriggerWrapper>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => {
  const hasStickyElements = React.useMemo(() => {
    const checkForStickyElements = (children: React.ReactNode): boolean => {
      if (!children) return false
      if (Array.isArray(children)) return children.some(checkForStickyElements)

      if (React.isValidElement(children)) {
        const displayName = (
          children.type as React.ComponentType<{ children?: React.ReactNode }>
        )?.displayName
        if (displayName === 'SelectActions' || displayName === 'SelectFooter')
          return true
        return checkForStickyElements(
          (children.props as { children?: React.ReactNode })?.children,
        )
      }

      return false
    }

    return checkForStickyElements(children)
  }, [children])

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'z-[101] flex max-h-[var(--radix-select-content-available-height)] min-w-[8rem] flex-col rounded-lg border border-gray-200 bg-white text-gray-950',
          'shadow-[0px_0px_12px_0px_var(--alpha-black-10)]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out ',
          'data-[state=open]:fade-in data-[state=open]:slide-in-from-top-2 ',
          'data-[state=closed]:fade-out data-[state=closed]:slide-out-to-top-2 ',
          'data-[state=closed]:duration-150 data-[state=open]:duration-150 ',
          'data-[state=closed]:[animation-timing-function:ease-in] data-[state=open]:[animation-timing-function:ease-out]',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectPrimitive.Viewport
          className={cn(
            'flex flex-col gap-xxxs overflow-y-auto px-xxs',
            {
              'py-xxs': !hasStickyElements,
            },
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('text-sm font-medium text-gray-900', className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    showCheck?: boolean
    checkPosition?: 'left' | 'right'
  }
>(
  (
    {
      className,
      showCheck = true,
      checkPosition = 'right',
      children,
      ...props
    },
    ref,
  ) => {
    const indicator = (
      <SelectPrimitive.ItemIndicator>
        <Check size={20} />
      </SelectPrimitive.ItemIndicator>
    )

    return (
      <SelectPrimitive.Item
        ref={ref}
        className={cn(
          'relative flex cursor-default select-none items-center gap-xxs rounded-m p-xxs',
          'text-base font-medium text-gray-900 outline-none transition-colors hover:bg-gray-100',
          'focus:bg-gray-100 focus:text-gray-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
          className,
        )}
        {...props}
      >
        {showCheck && checkPosition === 'left' && indicator}
        <div className="flex flex-1 items-center gap-xxs">
          <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        </div>
        {showCheck && checkPosition === 'right' && indicator}
      </SelectPrimitive.Item>
    )
  },
)
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-xxxs my-xxxs h-px bg-gray-200', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

type TUseVisibleItemsCount = Pick<ExtraSelectTriggerProps, 'onRemoveValue'> & {
  selectedValues: ExtraSelectTriggerProps['selectedValues']
}

const useVisibleItemsCount = ({
  selectedValues = [],
  onRemoveValue,
}: TUseVisibleItemsCount) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = React.useState(selectedValues.length)

  React.useLayoutEffect(() => {
    if (!containerRef.current || selectedValues.length === 0) {
      setVisibleCount(selectedValues.length)
      return
    }

    const container = containerRef.current
    const containerWidth = container.offsetWidth
    const paddingAndIconSpace = 60
    const availableWidth = containerWidth - paddingAndIconSpace

    const tempContainer = document.createElement('div')
    Object.assign(tempContainer.style, {
      position: 'absolute',
      visibility: 'hidden',
      whiteSpace: 'nowrap',
      display: 'flex',
      gap: '4px',
      top: '-9999px',
      left: '-9999px',
    })
    document.body.appendChild(tempContainer)

    let totalWidth = 0
    let count = 0

    for (const value of selectedValues) {
      const itemElement = document.createElement('div')
      itemElement.className =
        'gap-xxxs p-xxs flex cursor-pointer items-center rounded-sm bg-gray-100 text-sm font-medium text-gray-900'

      const textSpan = document.createElement('span')
      textSpan.textContent = typeof value === 'string' ? value : value.value
      itemElement.appendChild(textSpan)

      if (typeof value !== 'string' && value.avatar) {
        const avatarElement = document.createElement('div')
        avatarElement.className =
          'size-xs rounded-full bg-gray-300 flex-shrink-0'
        itemElement.insertBefore(avatarElement, textSpan)
      }

      if (onRemoveValue) {
        const removeButton = document.createElement('button')
        removeButton.innerHTML = '×'
        removeButton.className = 'ml-xxxs rounded-full outline-none'
        itemElement.appendChild(removeButton)
      }

      tempContainer.appendChild(itemElement)
      const itemWidth = itemElement.offsetWidth + 4

      if (totalWidth + itemWidth <= availableWidth) {
        totalWidth += itemWidth
        count++
      } else {
        break
      }
    }

    document.body.removeChild(tempContainer)
    setVisibleCount(Math.max(1, count))
  }, [selectedValues, onRemoveValue])

  return { containerRef, visibleCount }
}

type TSelectHiddenChipsCounter = Pick<
  ExtraSelectTriggerProps,
  'onRemoveValue'
> & {
  hiddenCount: number
  selectedValues: ExtraSelectTriggerProps['selectedValues']
}

const SelectHiddenChipsCounter: React.FC<TSelectHiddenChipsCounter> = ({
  hiddenCount,
  selectedValues = [],
  onRemoveValue,
}) => {
  const handleStopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <div
      onPointerDown={handleStopPropagation}
      onMouseDown={handleStopPropagation}
      onClick={handleStopPropagation}
    >
      <Select name="hidden-chips-counter">
        <SelectPrimitive.Trigger asChild>
          <div>
            <ChipsItem text={`+${hiddenCount}`} />
          </div>
        </SelectPrimitive.Trigger>
        <SelectContent
          align="end"
          className="max-h-[250px] w-screen max-w-[300px] gap-xxxs overflow-y-auto"
        >
          {selectedValues.map((value) => {
            const chipsValue = typeof value === 'string' ? value : value.value

            return (
              <div
                key={chipsValue}
                className="flex justify-between p-xxs"
                onClick={(e) => e.stopPropagation()}
              >
                <span>{chipsValue}</span>
                <X
                  size={16}
                  className="cursor-pointer"
                  onClick={() => onRemoveValue?.(chipsValue)}
                />
              </div>
            )
          })}
        </SelectContent>
      </Select>
    </div>
  )
}
SelectHiddenChipsCounter.displayName = 'SelectHiddenChipsCounter'

type ExtraSelectTriggerProps = {
  selectedValues?: { value: string; avatar: string }[] | string[]
  placeholder?: string
  onRemoveValue?: (value: string) => void
  caption?: React.ReactNode
  error?: React.ReactNode
  icon?: React.ReactNode
}

const SelectMultiTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    ExtraSelectTriggerProps
>((props, ref) => {
  const {
    className,
    selectedValues = [],
    placeholder,
    onRemoveValue,
    icon,
    caption,
    error,
    ...other
  } = props
  const { containerRef, visibleCount } = useVisibleItemsCount({
    selectedValues,
    onRemoveValue,
  })
  const hiddenCount = selectedValues.length - visibleCount

  const renderChips = () => {
    if (selectedValues.length === 0) {
      return <span>{placeholder || 'Select options...'}</span>
    }

    return (
      <ChipsWrapper>
        {selectedValues.slice(0, visibleCount).map((value) => {
          const chipsValue = typeof value === 'string' ? value : value.value
          return (
            <ChipsItem
              key={chipsValue}
              avatarSrc={typeof value === 'string' ? undefined : value.avatar}
              text={chipsValue}
              onRemove={() => onRemoveValue?.(chipsValue)}
            />
          )
        })}
        {hiddenCount > 0 && (
          <SelectHiddenChipsCounter
            hiddenCount={hiddenCount}
            onRemoveValue={onRemoveValue}
            selectedValues={selectedValues}
          />
        )}
      </ChipsWrapper>
    )
  }

  return (
    <SelectTriggerWrapper
      ref={ref}
      className={className}
      caption={caption}
      error={error}
      icon={icon}
      {...other}
    >
      <div
        ref={containerRef}
        className="flex w-full flex-1 flex-nowrap overflow-hidden"
      >
        {renderChips()}
      </div>
    </SelectTriggerWrapper>
  )
})
SelectMultiTrigger.displayName = 'SelectMultiTrigger'

const SelectCheckboxItem = React.forwardRef<
  HTMLDivElement,
  {
    value: string
    isSelected?: boolean
    isLoading?: boolean
    checkboxPosition?: 'left' | 'right'
    onSelect?: (value: string) => void
    className?: string
    children: React.ReactNode
  }
>(
  (
    {
      value,
      isSelected = false,
      isLoading = false,
      checkboxPosition = 'right',
      onSelect,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const indicator = isLoading ? (
      <Loader2 size={24} className="flex-shrink-0 animate-spin text-blue-500" />
    ) : (
      <Checkbox size="default" variant="default" checked={isSelected} />
    )

    const handleClick = () => {
      onSelect?.(value)
    }

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex cursor-pointer select-none items-center gap-xxs rounded-m',
          'p-xxs text-base font-medium text-gray-900 outline-none transition-colors hover:bg-gray-100',
          'focus:bg-gray-100 focus:text-gray-900',
          { 'pointer-events-none': isLoading },
          className,
        )}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleClick()
          }
        }}
        {...props}
      >
        {checkboxPosition === 'left' && indicator}
        <div className="flex flex-1 items-center gap-xxs">{children}</div>
        {checkboxPosition === 'right' && indicator}
      </div>
    )
  },
)
SelectCheckboxItem.displayName = 'SelectCheckboxItem'

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center py-xxs',
      className,
    )}
    {...props}
  >
    <ChevronUp size={16} />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      'flex cursor-default items-center justify-center p-xxs',
      className,
    )}
    {...props}
  >
    <ChevronDown size={16} />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName

const SelectActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('sticky top-0 z-20 bg-white p-xxs', className)}
    {...props}
  >
    {children}
  </div>
))
SelectActions.displayName = 'SelectActions'

const SelectFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('sticky bottom-0 z-20 bg-white p-xxs', className)}
    {...props}
  >
    {children}
  </div>
))
SelectFooter.displayName = 'SelectFooter'

export {
  Select,
  SelectValue,
  SelectGroup,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectMultiTrigger,
  SelectCheckboxItem,
  SelectScrollUpButton,
  SelectScrollDownButton,
  SelectActions,
  SelectFooter,
}
